import { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from "react";
import classes from "./teamFormModal.module.scss";
import { teamService } from "../../../../entities/team";
import type { ITeam, ITeamUpsertData } from "../../../../entities/team";
import { workTgService } from "../../../../entities/workTg";
import { idmService } from "../../../../entities/idm";
import { Modal } from "../../../../shared/ui/modal";
import { MyInput } from "../../../../shared/ui/input";
import { DropDownListSelected } from "../../../../shared/ui/dropDownSelected";
import { IItem } from "../../../../shared/model/types";
import { MyButton } from "../../../../shared/ui/button";
import { ITeamFormState, toUniqueSorted } from "../../lib/types";
import { useTeamsCommon } from "../../lib/useTeamsCommon";
import { MyError } from "../../../../shared/lib/error/MyError";

interface IProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    editingTeamId: string | null;
    form: ITeamFormState;
    setForm: Dispatch<SetStateAction<ITeamFormState>>;
    formError: string;
    setFormError: (value: string) => void;
    onSaveSuccess: (team: ITeam, isEdit: boolean) => void;
}

export const TeamFormModal: FC<IProps> = ({open, setOpen, editingTeamId, form, setForm, formError, setFormError, onSaveSuccess}) => {
    const [coordinatorOptions, setCoordinatorOptions] = useState<IItem[]>([])
    const [curatorOptionsByTgId, setCuratorOptionsByTgId] = useState<Record<number, IItem[]>>({})
    const [tgOptions, setTgOptions] = useState<IItem[]>([])
    const [optionsLoading, setOptionsLoading] = useState(false)
    const {setGlobalLoading, handleError} = useTeamsCommon()

    const selectedTgItems = form.tgAccounts
    const selectedCoordinatorItem = form.coordinator
    const selectedWorkTgIds = useMemo(
        () => selectedTgItems.map(item => Number(item.id)).filter(id => !Number.isNaN(id) && id > 0),
        [selectedTgItems]
    )
    const selectedWorkTgIdsKey = selectedWorkTgIds.join(',')

    const loadOptions = async () => {
        try{
            setOptionsLoading(true)
            const [workTgs, coordinatorsRes, curatorsResponses] = await Promise.all([
                workTgService.getAll(),
                idmService.listAvailableCoordinators(selectedWorkTgIds),
                Promise.all(selectedWorkTgIds.map(async (workTgId) => ({
                    workTgId,
                    response: await idmService.listAvailableCurators([workTgId])
                })))
            ])

            setTgOptions(
                workTgs
                    .map(item => ({id: item.id, name: item.name}))
                    .sort((a, b) => a.name.localeCompare(b.name))
            )
            setCoordinatorOptions(
                coordinatorsRes.items
                    .map(item => ({id: item.user_id, name: `${item.user_id}. ${item.full_name}`.trim()}))
                    .sort((a, b) => a.name.localeCompare(b.name))
            )
            setCuratorOptionsByTgId(
                curatorsResponses.reduce<Record<number, IItem[]>>((acc, item) => {
                    acc[item.workTgId] = item.response.items
                        .map(curator => ({id: curator.user_id, name: `${curator.user_id}. ${curator.full_name}`.trim()}))
                        .sort((a, b) => a.name.localeCompare(b.name))
                    return acc
                }, {})
            )
        }
        catch(e){
            handleError(e, 'Ошибка при получении данных для формы звена')
        }
        finally{
            setOptionsLoading(false)
        }
    }

    useEffect(() => {
        if(open){
            loadOptions()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, selectedWorkTgIdsKey])

    const syncFormForSelectedTgs = (nextTgItems: IItem[]) => {
        const nextIds = nextTgItems.map(item => Number(item.id))
        setForm(current => {
            const nextCuratorIds: Record<number, number[]> = {}

            nextIds.forEach(id => {
                nextCuratorIds[id] = current.tgCuratorIds[id] ?? []
            })

            return {
                ...current,
                tgAccounts: nextTgItems,
                tgCuratorIds: nextCuratorIds,
            }
        })
    }

    const getCuratorOptionsForTg = (tgAccountId: number) => {
        const curatorIdsSelectedForOtherTgs = new Set(
            Object.entries(form.tgCuratorIds)
                .filter(([selectedTgAccountId]) => Number(selectedTgAccountId) !== tgAccountId)
                .flatMap(([, curatorUserIds]) => curatorUserIds)
        )

        return (curatorOptionsByTgId[tgAccountId] ?? [])
            .filter(item => !curatorIdsSelectedForOtherTgs.has(Number(item.id)))
    }

    const buildPayload = (): ITeamUpsertData | null => {
        const name = form.name.trim()
        const tgAccountIds = toUniqueSorted(form.tgAccounts.map(item => Number(item.id)).filter(id => id > 0))
        const coordinatorUserId = Number(form.coordinator?.id ?? 0)

        if(!name){
            setFormError('Введите название звена')
            return null
        }

        if(tgAccountIds.length < 1){
            setFormError('Нужно выбрать хотя бы один рабочий ТГ')
            return null
        }

        if(coordinatorUserId <= 0){
            setFormError('Нужно выбрать координатора звена')
            return null
        }

        const tgCurators = tgAccountIds.map(tgAccountId => {
            const curatorUserIds = toUniqueSorted((form.tgCuratorIds[tgAccountId] ?? []).map(Number).filter(id => id > 0))

            return {
                tgAccountId,
                coordinatorUserId,
                curatorUserIds,
            }
        })

        const curatorUserIds = tgCurators.flatMap(item => item.curatorUserIds)
        if(new Set(curatorUserIds).size !== curatorUserIds.length){
            setFormError('Один куратор не может одновременно работать на двух ТГ одного звена')
            return null
        }

        setFormError('')

        return {
            name,
            tgAccountIds,
            coordinatorUserIds: [coordinatorUserId],
            tgCurators,
        }
    }

    const onSave = async () => {
        const payload = buildPayload()
        if(!payload){
            return
        }

        try{
            setGlobalLoading(true)
            const team = editingTeamId
                ? await teamService.update(editingTeamId, payload)
                : await teamService.create(payload)
            setOpen(false)
            onSaveSuccess(team, Boolean(editingTeamId))
        }
        catch(e){
            if(e instanceof MyError){
                setFormError(e.message || (editingTeamId ? 'Ошибка при обновлении звена' : 'Ошибка при создании звена'))
            }
            handleError(e, editingTeamId ? 'Ошибка при обновлении звена' : 'Ошибка при создании звена')
        }
        finally{
            setGlobalLoading(false)
        }
    }

    return (
        <Modal open={open} setOpen={setOpen}>
            <section className={classes.modal}>
                <section className={classes.modalTitle}>{editingTeamId ? 'Редактировать звено' : 'Создать звено'}</section>
                <section className={classes.formGrid}>
                    <MyInput
                        title="Название звена"
                        value={form.name}
                        setValue={(value) => setForm(current => ({...current, name: value}))}
                        setError={setFormError}
                        error={formError}
                        placeholder="Например: Звено Север"
                    />

                    <section>
                        <section className={classes.sectionTitle}>Рабочие ТГ</section>
                        <DropDownListSelected
                            isLoading={optionsLoading}
                            items={tgOptions}
                            selectedIdItems={selectedTgItems.map(item => item.id)}
                            onSelected={(item) => (selected) => {
                                const currentItems = selectedTgItems.filter(selectedItem => selectedItem.id !== item.id)
                                const nextItems = selected ? [...currentItems, item].slice(-2) : currentItems
                                syncFormForSelectedTgs(nextItems)
                                setFormError('')
                            }}
                            selectedCount
                            search
                            placeholder="Выберите рабочие ТГ"
                            searchPlaceholder="Поиск по рабочим ТГ"
                        />
                    </section>

                    <section>
                        <section className={classes.sectionTitle}>Координатор звена</section>
                        <DropDownListSelected
                            isLoading={optionsLoading}
                            items={
                                selectedCoordinatorItem && !coordinatorOptions.find(item => item.id === selectedCoordinatorItem.id)
                                    ? [selectedCoordinatorItem, ...coordinatorOptions]
                                    : coordinatorOptions
                            }
                            selectedIdItems={selectedCoordinatorItem ? [selectedCoordinatorItem.id] : []}
                            onSelected={(item) => (selected) => {
                                setForm(current => ({...current, coordinator: selected ? item : null}))
                                setFormError('')
                            }}
                            search
                            placeholder="Выберите координатора"
                            searchPlaceholder="Поиск по сотрудникам"
                        />
                    </section>

                    <section>
                        <section className={classes.sectionTitle}>Состав по Telegram</section>
                        <section className={classes.tgConfigList}>
                            {selectedTgItems.map(tgItem => (
                                <section key={tgItem.id} className={classes.tgConfigCard}>
                                    <section className={classes.tgConfigHeader}>{tgItem.name}</section>
                                    <section className={classes.formGrid}>
                                        <section>
                                            <section className={classes.infoLabel}>Координатор</section>
                                            <section className={classes.itemMeta}>
                                                {selectedCoordinatorItem?.name || 'Не выбран'}
                                            </section>
                                        </section>

                                        <section>
                                            <section className={classes.infoLabel}>
                                Кураторы {(form.tgCuratorIds[Number(tgItem.id)] ?? []).length}
                                            </section>
                                            <DropDownListSelected
                                                isLoading={optionsLoading}
                                                items={getCuratorOptionsForTg(Number(tgItem.id))}
                                                selectedIdItems={form.tgCuratorIds[Number(tgItem.id)] ?? []}
                                                onSelected={(item) => (selected) => {
                                                    const currentIds = form.tgCuratorIds[Number(tgItem.id)] ?? []
                                                    const nextIds = selected
                                                        ? [...currentIds.filter(id => id !== Number(item.id)), Number(item.id)]
                                                        : currentIds.filter(id => id !== Number(item.id))

                                                    setForm(current => ({
                                                        ...current,
                                                        tgCuratorIds: {
                                                            ...current.tgCuratorIds,
                                                            [Number(tgItem.id)]: nextIds,
                                                        }
                                                    }))
                                                    setFormError('')
                                                }}
                                                selectedCount
                                                search
                                                placeholder="Выберите кураторов"
                                                searchPlaceholder="Поиск кураторов"
                                            />
                                        </section>
                                    </section>
                                </section>
                            ))}
                            {selectedTgItems.length === 0 && (
                                <section className={classes.placeholder}>Сначала выберите хотя бы один рабочий ТГ</section>
                            )}
                        </section>
                    </section>
                </section>

                <section className={classes.formActions}>
                    <MyButton onClick={onSave} error={formError}>Сохранить</MyButton>
                </section>
            </section>
        </Modal>
    )
}
